import React from 'react';
import './email-list-view.scss';

export interface EmailListItem {
  id: string;
  pill?: {
    text: string;
    variant:
      | 'default'
      | 'green'
      | 'red'
      | 'blue'
      | 'yellow'
      | 'light-blue'
      | 'beige';
  };
  address: string;
  date: string | React.ReactNode;
  subject: string;
  snippet: string;
  sortDate?: string; // Optional field for sorting, not displayed
}

export interface EmailListViewProps {
  items: EmailListItem[];
  showPills?: boolean;
  onItemClick?: (item: EmailListItem) => void;
  renderItemActions?: (item: EmailListItem) => React.ReactNode;
  emptyHeader?: string;
  emptyText?: string;
}

const EmailListView: React.FC<EmailListViewProps> = ({
  items,
  showPills = false,
  onItemClick,
  renderItemActions,
  emptyHeader = 'No emails found',
  emptyText = 'No emails found',
}) => {
  const parseAddress = (address: string) => {
    // Match pattern: "Name <email>" or just "email"
    const match = address.match(/^(.+?)\s*<(.+?)>$/);
    if (match) {
      return {
        name: match[1].trim(),
        email: match[2].trim(),
      };
    }
    return { name: null, email: address };
  };

  if (!items.length) {
    return (
      <div className='email-list-view'>
        <div className='empty-state-wrapper'>
          <h4>{emptyHeader}</h4>
          <p className='empty-text'>{emptyText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='email-list-view'>
      {items.map((item) => {
        const parsedAddress = parseAddress(item.address);

        return (
          <div
            key={item.id}
            className='email-item'
            onClick={() => onItemClick?.(item)}
            style={{ cursor: onItemClick ? 'pointer' : 'default' }}
          >
            <div className='email-item-inner'>
              {/* Header row with pill, address, and date */}
              {showPills && item.pill && (
                <div className='email-pill-container'>
                  <span
                    className={`email-pill email-pill--${item.pill.variant}`}
                  >
                    {item.pill.text}
                  </span>
                </div>
              )}
              <div className='email-header'>
                <div
                  className={`email-address ${parsedAddress.name ? 'with-name' : ''}`}
                >
                  {parsedAddress.name ? (
                    <>
                      <strong>{parsedAddress.name}</strong>
                      <span> {parsedAddress.email}</span>
                    </>
                  ) : (
                    parsedAddress.email
                  )}
                </div>
                <div className='email-date'>{item.date}</div>
              </div>

              {/* Content row with subject */}
              <div className='email-content'>
                <div className='email-subject'>{item.subject}</div>
                <div className='email-snippet'>{item.snippet}</div>
              </div>
            </div>

            {/* Optional actions/comments rendered by parent */}
            {renderItemActions?.(item)}
          </div>
        );
      })}
    </div>
  );
};

export default EmailListView;
